import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridToolbarContainer, gridClasses } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useBatchGetClientsQuery } from '@/features/clients/clientsApi';
import { useBatchGetDealsQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useSearchAuditActionsQuery } from '@/features/audit/auditApi';
import { useAuditUserFilters } from '@/hooks/useAuditUserFilters';
import { 
	getDefaultAuditDateRange, 
	setFromDate, 
	setToDate } from '@/features/table/auditTableSlice';
import { TableSortContextProvider, useTableSort } from '@/context/tableSortContext';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderButton from '@/components/table/DataGridComponents/DataGridColumnReorderButton';
import { AuditTableFilters } from './AuditTableFilters';
import { AuditExportButton } from './AuditExportButton';
import { columns } from './data';
import { mergeAuditRowsWithEntityNames, sortAuditRows } from './auditRowUtils';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '@/components/table/DataGridComponents/NoRowsOverlay';
import InfiniteTablePagination from '../InfiniteTablePagination';

const AuditNoRowsOverlay = () => <NoRowsOverlay label='No audit records found.' />;

const AuditNoRowsErrorOverlay = () => <NoRowsErrorOverlay objectType='audit records' />;

const AuditToolbar = ({ exportParams, exportDisabled, onRefresh, refreshColor }) => (
	<GridToolbarContainer sx={{ px: 1 }}>
		<DataGridColumnReorderButton />
		<AuditExportButton searchParams={exportParams} disabled={exportDisabled} />
		<Box sx={{ flexGrow: 1 }} />
		<IconButton size='small' onClick={onRefresh}>
			<RefreshIcon sx={{ fontSize: '1.25rem', color: refreshColor }} />
		</IconButton>
	</GridToolbarContainer>
);

const AuditTableContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const filters = useSelector((state) => state.auditTablePrefs);
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { sortModel, setSortModel } = useTableSort();
	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(columns);
	const [refreshIndicator, setRefreshIndicator] = useState(0);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const userId = userDetails?.userId;
	const { userIds } = useAuditUserFilters();

	useEffect(() => {
		if (!filters.fromDate) {
			dispatch(setFromDate(getDefaultAuditDateRange().fromDate));
		}
		if (!filters.toDate) {
			dispatch(setToDate(getDefaultAuditDateRange().toDate));
		}
	}, [dispatch, filters.fromDate, filters.toDate]);

	const searchParams = useMemo(
		() => ({
			dateFrom: filters.fromDate,
			dateTo: filters.toDate,
			userIds,
			actionTypes: filters.actionTypes ?? [],
			userId,
			clientId: filters.clientId,
			refreshIndicator,
		}),
		[
			filters.fromDate,
			filters.toDate,
			filters.actionTypes,
			userId,
			userIds,
			filters.clientId,
			refreshIndicator,
		],
	);

	useEffect(() => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	}, [
		filters.fromDate,
		filters.toDate,
		filters.userIds,
		filters.actionTypes,
		filters.clientId,
		sortModel,
	]);

	const { data, isFetching, isLoading, isError, refetch } = useSearchAuditActionsQuery(
		searchParams,
		{
			skip: !searchParams.dateFrom || !searchParams.dateTo || !userId,
		},
	);

	const rows = data?.data ?? [];

	const dealIds = useMemo(
		() =>
			Array.from(
				new Set(
					rows
						.map((row) => row.dealId)
						.filter(Boolean)
						.map(String),
				),
			),
		[rows],
	);

	const dealsQuery = useBatchGetDealsQuery(dealIds, { skip: dealIds.length === 0 });
	const deals = dealsQuery.data ?? [];

	const clientIds = useMemo(
		() =>
			Array.from(
				new Set(
					[
						...rows.map((row) => row.clientId),
						...deals.map((deal) => getDealClientId(deal)),
					]
						.filter(Boolean)
						.map(String),
				),
			),
		[rows, deals],
	);

	const clientsQuery = useBatchGetClientsQuery(clientIds, {
		skip: clientIds.length === 0 || (dealIds.length > 0 && dealsQuery.isLoading),
	});
	const clients = clientsQuery.data ?? [];

	const enrichedRows = useMemo(
		() => mergeAuditRowsWithEntityNames({ rows, clients, deals }),
		[rows, clients, deals],
	);

	const isEnriching =
		(dealIds.length > 0 && dealsQuery.isFetching) ||
		(clientIds.length > 0 && clientsQuery.isFetching);

	const sortedRows = useMemo(
		() => sortAuditRows(enrichedRows, sortModel),
		[enrichedRows, sortModel],
	);

	const pageRows = useMemo(() => {
		const start = paginationModel.page * paginationModel.pageSize;
		return sortedRows.slice(start, start + paginationModel.pageSize);
	}, [sortedRows, paginationModel.page, paginationModel.pageSize]);

	const triggerRefresh = () => setRefreshIndicator((prev) => prev + 1);

	const exportParams = useMemo(
		() => ({
			dateFrom: filters.fromDate,
			dateTo: filters.toDate,
			userIds,
			actionTypes: filters.actionTypes ?? [],
			userId,
			clientId: filters.clientId ?? null,
		}),
		[filters.fromDate, filters.toDate, filters.actionTypes, userId, userIds, filters.clientId],
	);

	const TablePagination = useCallback(() => {
		const totalPages = Math.max(1, Math.ceil(sortedRows.length / paginationModel.pageSize));

		return (
			<InfiniteTablePagination
				count={sortedRows.length}
				page={paginationModel.page}
				setPage={(page) => setPaginationModel((prev) => ({ ...prev, page }))}
				rowsPerPage={paginationModel.pageSize}
				hasNextPage={paginationModel.page + 1 < totalPages}
				hasPreviousPage={paginationModel.page > 0}
			/>
		);
	}, [paginationModel.page, paginationModel.pageSize, sortedRows.length]);

	return (
		<Stack spacing={2} sx={{ width: '100%' }}>
			<AuditTableFilters disabled={isLoading} />
			<Box
				className='MuiDataGrid-parentDiv'
				sx={{
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					backgroundColor: theme.palette.background.paper,
					display: 'flex',
					flexDirection: 'column',
					minHeight: '12em',
					maxHeight: 'calc(100vh - 280px)',
					'& .MuiDataGrid-root': {
						borderRadius: '8px',
					},
				}}
			>
				<DataGrid
					rows={pageRows}
					columns={orderedColumns}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={onColumnVisibilityModelChange}
					onColumnWidthChange={onColumnWidthChange}
					getRowId={(row) => row.id}
					getRowHeight={() => 'auto'}
					loading={isLoading || isFetching || isEnriching}
					disableRowSelectionOnClick
					sortingMode='server'
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					slots={{
						toolbar: AuditToolbar,
						pagination: TablePagination,
						noRowsOverlay: isError ? AuditNoRowsErrorOverlay : AuditNoRowsOverlay,
						columnMenu: DataGridColumnMenu,
					}}
					slotProps={{
						loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
						toolbar: {
							exportParams,
							exportDisabled: !enrichedRows.length || isFetching,
							onRefresh: () => {
								triggerRefresh();
								refetch();
							},
							refreshColor: theme.palette.primary.main,
						},
					}}
					hideFooterSelectedRowCount
					sx={{
						backgroundColor: theme.palette.background.paper,
						border: 'none',
						borderRadius: '8px',
						minHeight: '8em',
						height: pageRows.length ? 'auto' : '12em',
						'--DataGrid-overlayHeight': '3em',
						[`& .${gridClasses.columnHeader}`]: {
							borderRight: 'none !important',
						},
						'& .MuiDataGrid-cell': {
							padding: 1,
							minHeight: '42px',
						},
						'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
							outline: 'none',
						},
						[`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
							{
								outline: 'none',
							},
						[`& .${gridClasses.columnHeader}`]: {
							borderRight: 'none !important',
						},
						'& .MuiDataGrid-overlayWrapperInner': {
							minHeight: '5em',
						},
					}}
				/>
			</Box>
		</Stack>
	);
};

const AuditTable = () => (
	<ColumnControlContextProvider variant='audit' value='list'>
		<TableSortContextProvider value='audit' variant='audit'>
			<AuditTableContent />
		</TableSortContextProvider>
	</ColumnControlContextProvider>
);

export default AuditTable;
